import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";
import type { Vec3 } from "../wanim/parse.ts";
import { bindWorldPositions } from "./clip.ts";
import { MOTIONBUILDER_NAMES } from "./skeleton.ts";
import { resolveUnityName } from "./boneResolve.ts";
import type { SkinnedMeshExport } from "../fbx/animationFbx.ts";

/**
 * Weighted body mesh, retargeted onto the recording's skeleton.
 *
 * The bundled body is Quaternius' "Animated Base Character" (CC0) ??? a smooth
 * skinned human. Pipeline (works for ANY humanoid GLB, regardless of how its
 * bind matrices were exported):
 *   1. map source bone names ??? our HumanBodyBones indices,
 *   2. FK-align the source skeleton's rest pose to OUR T-pose (rotate each
 *      bone so its segment direction matches ours ??? fixes A-poses and facing),
 *   3. evaluate the skin CPU-side in that aligned pose (true skinning math),
 *   4. transfer each vertex: offset from its bone's joint, STRETCHED along
 *      the bone axis by the segment-length ratio (girth preserved), placed at
 *      our joint, blended by the original weights.
 * Head/eye geometry is dropped ??? the Face mesh replaces it.
 */
export interface BodyMeshData {
  name: string;
  /** World-space positions in OUR T-pose bind (meters). */
  positions: Float32Array;
  normals: Float32Array;
  indices: Uint32Array;
  /** Per-vertex 4 influences: OUR bone indices + weights. */
  skinIndex: Uint16Array;
  skinWeight: Float32Array;
  /** Morph channels matched to recorded blendshape names (deltas in meters). */
  channels?: { name: string; deltas: Float32Array }[];
  /** Per-vertex UVs (2 per vertex), when the source mesh has them. */
  uv?: Float32Array;
  /** Source material's base-color texture for the preview, if any. */
  map?: THREE.Texture | null;
  /** Raw base-color image bytes (PNG/JPEG from the GLB) for FBX embedding. */
  texture?: { bytes: Uint8Array; mime: string };
}

interface BodySource {
  scene: THREE.Object3D;
  /** Explicit bone mapping (VRM humanoid): bone Object3D → Unity bone name. */
  boneUnity: Map<THREE.Object3D, string> | null;
  /** Per-mesh base-color image bytes from the GLB (for FBX embedding). */
  meshTexture: Map<THREE.Object3D, { bytes: Uint8Array; mime: string }> | null;
}

/**
 * Pull each skinned mesh's base-color image bytes straight from the GLB BIN
 * chunk (no canvas re-encode), via three's parser associations.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractGlbTextures(gltf: any, chunks: { json: any; bin: Uint8Array | null }): Map<THREE.Object3D, { bytes: Uint8Array; mime: string }> {
  const out = new Map<THREE.Object3D, { bytes: Uint8Array; mime: string }>();
  if (!chunks.bin) return out;
  const json = chunks.json;
  const assoc: Map<THREE.Object3D, { meshes?: number; primitives?: number }> = gltf.parser.associations;
  assoc.forEach((v, obj) => {
    if (v?.meshes === undefined || !(obj as THREE.SkinnedMesh).isSkinnedMesh) return;
    const prim = json.meshes?.[v.meshes]?.primitives?.[v.primitives ?? 0];
    const mat = json.materials?.[prim?.material];
    const texIndex = mat?.pbrMetallicRoughness?.baseColorTexture?.index;
    if (texIndex === undefined) return;
    const img = json.images?.[json.textures?.[texIndex]?.source];
    const bv = json.bufferViews?.[img?.bufferView];
    if (!bv) return;
    const bytes = chunks.bin!.subarray(bv.byteOffset ?? 0, (bv.byteOffset ?? 0) + bv.byteLength);
    out.set(obj, { bytes, mime: img.mimeType ?? "image/png" });
  });
  return out;
}

let gltfCache: Promise<BodySource> | null = null;
let userSource: BodySource | null = null;

function loadBundledGltf(): Promise<BodySource> {
  if (gltfCache) return gltfCache;
  const loader = new GLTFLoader();
  loader.setMeshoptDecoder(MeshoptDecoder);
  // ?v= busts the Pages/browser cache ??? bump when swapping the bundled asset.
  gltfCache = loader
    .loadAsync(`${import.meta.env.BASE_URL}body.glb?v=xbot1`)
    .then((g) => ({ scene: g.scene, boneUnity: null, meshTexture: null }));
  return gltfCache;
}

function getActiveSource(): Promise<BodySource> {
  return userSource ? Promise.resolve(userSource) : loadBundledGltf();
}

/** Build a Object3D???Unity map from a parsed GLTF + a node-index humanoid map. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function boneUnityFromAssociations(gltf: any, nodeMap: Map<number, string>): Map<THREE.Object3D, string> {
  const out = new Map<THREE.Object3D, string>();
  const assoc: Map<THREE.Object3D, { nodes?: number }> = gltf.parser.associations;
  assoc.forEach((v, obj) => {
    if (v && typeof v.nodes === "number") {
      const unity = nodeMap.get(v.nodes);
      if (unity) out.set(obj, unity);
    }
  });
  return out;
}

/**
 * Use a user-provided VRM/GLB as the body source (null restores the bundled
 * body). Returns the number of humanoid-mapped bones (0 = name-based mapping).
 */
/** True while a user-provided VRM/GLB is the active body source. */
export function hasUserBody(): boolean {
  return userSource !== null;
}

export async function setBodySource(buffer: ArrayBuffer | null): Promise<number> {
  if (buffer === null) {
    userSource = null;
    bodyCache = null;
    return 0;
  }
  const { sanitizeGlb, parseVrmHumanoid, parseGlbChunks } = await import("../vrm/vrmHumanoid.ts");
  const clean = sanitizeGlb(buffer);
  const nodeMap = parseVrmHumanoid(clean);
  const loader = new GLTFLoader();
  loader.setMeshoptDecoder(MeshoptDecoder);
  const gltf = await loader.parseAsync(clean, "");
  const boneUnity = nodeMap ? boneUnityFromAssociations(gltf, nodeMap) : null;
  const chunks = parseGlbChunks(clean);
  const meshTexture = chunks ? extractGlbTextures(gltf, chunks) : null;
  userSource = { scene: gltf.scene, boneUnity, meshTexture };
  bodyCache = null;
  return boneUnity?.size ?? 0;
}

export interface BodyExtract {
  meshes: BodyMeshData[];
  /** Source-skeleton joint world positions per OUR bone index (meters, after T-pose alignment), or null. */
  joints: (Vec3 | null)[];
}

// buildBodyData results depend on the skeleton's bind, so cache per-bind.
let bodyCache: { key: string; data: Promise<BodyExtract> } | null = null;

export function buildBodyData(
  parents: number[],
  bindPos: Vec3[],
  unityNames: string[],
  morphNames?: string[],
): Promise<BodyExtract> {
  const key =
    bindPos.map((p) => p.map((v) => v.toFixed(4)).join(",")).join(";") +
    `|m${morphNames?.length ?? 0}`;
  if (bodyCache?.key === key) return bodyCache.data;
  const data = getActiveSource().then((src) =>
    extractBodyMeshes(src.scene, parents, bindPos, unityNames, src.boneUnity ?? undefined, {
      // A user's VRM keeps its own head/hair (facecap Face suppressed) and
      // its morphs are matched to the recorded blendshape names.
      keepHead: userSource !== null,
      morphNames,
      textures: src.meshTexture ?? undefined,
    }),
  );
  bodyCache = { key, data };
  return data;
}

/** Pure step (also usable from node checks on a parsed GLTF scene). */
export function extractBodyMeshes(
  scene: THREE.Object3D,
  parents: number[],
  bindPos: Vec3[],
  unityNames: string[],
  boneUnity?: Map<THREE.Object3D, string>,
  opts: {
    keepHead?: boolean;
    morphNames?: string[];
    textures?: Map<THREE.Object3D, { bytes: Uint8Array; mime: string }>;
  } = {},
): BodyExtract {
  scene.updateWorldMatrix(true, true);
  const ourWorld = bindWorldPositions(parents, bindPos);

  // Unity name ??? our index, plus HumanIK names (covers Mixamo-style rigs).
  const nameToIndex = new Map<string, number>();
  unityNames.forEach((u, i) => {
    nameToIndex.set(u, i);
    nameToIndex.set(MOTIONBUILDER_NAMES[u] ?? u, i);
  });

  const skinnedMeshes: THREE.SkinnedMesh[] = [];
  scene.traverse((o) => {
    const m = o as THREE.SkinnedMesh;
    if (m.isSkinnedMesh) skinnedMeshes.push(m);
  });
  if (skinnedMeshes.length === 0) return { meshes: [], joints: unityNames.map(() => null) };
  // UNION of all skeletons' bones ??? VRMs often bind body and hair/clothing
  // meshes to DIFFERENT Skeleton objects; resolving every mesh's skin indices
  // against the first skeleton shreds the others (hair ribbons).
  const allBones: THREE.Bone[] = [];
  const unionIndex = new Map<THREE.Bone, number>();
  for (const sm of skinnedMeshes) {
    for (const b of sm.skeleton.bones) {
      if (!unionIndex.has(b)) {
        unionIndex.set(b, allBones.length);
        allBones.push(b);
      }
    }
  }
  const skeleton = { bones: allBones };
  const localToUnion = (sm: THREE.SkinnedMesh) =>
    sm.skeleton.bones.map((b) => unionIndex.get(b) ?? -1);

  // Capture the REST state in plain world space, using the asset's OWN
  // skinning for the rest shape (whatever its bind convention ??? 2017-era
  // Quaternius and some DCC exports ship degenerate inverse binds, so we
  // never rely on them after this point).
  for (const m of skinnedMeshes) m.skeleton.update();
  const restWorldVerts = skinnedMeshes.map((m) => {
    const pos = m.geometry.getAttribute("position");
    const out = new Float32Array(pos.count * 3);
    const v = new THREE.Vector3();
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i);
      m.applyBoneTransform(i, v);
      m.localToWorld(v);
      out[i * 3] = v.x; out[i * 3 + 1] = v.y; out[i * 3 + 2] = v.z;
    }
    return out;
  });
  const childBones = (b: THREE.Bone) =>
    b.children.filter((c) => (c as THREE.Bone).isBone) as THREE.Bone[];
  const segLen = (a: Vec3, b: Vec3) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
  const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);

  // Rest-space direction from each Foot joint toward its foot-vertex centroid
  // (used to orient feet on rigs without toe bones).
  const footRestDir = new Map<number, THREE.Vector3>();
  {
    const sums = new Map<number, { c: THREE.Vector3; n: number }>();
    skinnedMeshes.forEach((m, mi3) => {
      const sIdx = m.geometry.getAttribute("skinIndex");
      const sWgt = m.geometry.getAttribute("skinWeight");
      const rest = restWorldVerts[mi3];
      const l2u = localToUnion(m);
      for (let i = 0; i < rest.length / 3; i++) {
        for (let k = 0; k < 4; k++) {
          const j = l2u[sIdx.getComponent(i, k)];
          if (j < 0 || sWgt.getComponent(i, k) < 0.4) continue;
          if (!/Foot/.test(skeleton.bones[j]?.name ?? "")) continue;
          let e = sums.get(j);
          if (!e) sums.set(j, (e = { c: new THREE.Vector3(), n: 0 }));
          e.c.x += rest[i * 3]; e.c.y += rest[i * 3 + 1]; e.c.z += rest[i * 3 + 2];
          e.n++;
        }
      }
    });
    sums.forEach((e, j) => {
      const ankle = skeleton.bones[j].getWorldPosition(new THREE.Vector3());
      const dir = e.c.multiplyScalar(1 / e.n).sub(ankle);
      if (dir.lengthSq() > 1e-8) footRestDir.set(j, dir.normalize());
    });
  }

  // Resolve a source bone ??? our index: explicit humanoid map (VRM) first,
  // then all known naming families.
  const resolveBone = (obj: THREE.Object3D): number | undefined => {
    const explicit = boneUnity?.get(obj);
    if (explicit !== undefined) return nameToIndex.get(explicit);
    const unity = resolveUnityName(obj.name);
    return unity ? nameToIndex.get(unity) : undefined;
  };
  // Source bone ??? our bone (walk up through unmapped helpers like HeadTop_End).
  const boneOurIndex = skeleton.bones.map((b) => {
    let cur: THREE.Object3D | null = b;
    while (cur) {
      const idx = resolveBone(cur);
      if (idx !== undefined) return idx;
      cur = cur.parent;
    }
    return 0;
  });
  const directlyMapped = skeleton.bones.map((b) => resolveBone(b) !== undefined);

  const headIdx = unityNames.indexOf("Head");
  const eyeIdx = [unityNames.indexOf("LeftEye"), unityNames.indexOf("RightEye")];
  const boneIsHead = boneOurIndex.map((i) => i === headIdx || eyeIdx.includes(i));

  // Our skeleton's children + segment axes (T-pose).
  const ourChildrenAll: number[][] = unityNames.map(() => []);
  parents.forEach((p, i) => {
    if (p >= 0) ourChildrenAll[p].push(i);
  });
  // The HIPS axis uses the Spine child ONLY: the mean over spine+both legs is
  // near-degenerate and differs between skeletons, so the alignment rotation
  // can flip the pelvis (seen as "hips facing the wrong way" in recorded-
  // proportions mode).
  const spineIdx = unityNames.indexOf("Spine");
  const hipsIdx = unityNames.indexOf("Hips");
  const ourChildren: number[][] = ourChildrenAll.map((kids, m) =>
    m === hipsIdx && kids.includes(spineIdx) ? [spineIdx] : kids,
  );
  const ourAxis: (THREE.Vector3 | null)[] = unityNames.map((_, m) => {
    const dir = new THREE.Vector3();
    for (const c of ourChildren[m]) {
      dir.add(new THREE.Vector3(
        ourWorld[c][0] - ourWorld[m][0],
        ourWorld[c][1] - ourWorld[m][1],
        ourWorld[c][2] - ourWorld[m][2],
      ));
    }
    if (dir.lengthSq() < 1e-8 && parents[m] >= 0) {
      dir.set(
        ourWorld[m][0] - ourWorld[parents[m]][0],
        ourWorld[m][1] - ourWorld[parents[m]][1],
        ourWorld[m][2] - ourWorld[parents[m]][2],
      );
    }
    return dir.lengthSq() > 1e-8 ? dir.normalize() : null;
  });

  // --- 2+3. Closed-form world-space delta per bone (NO scene-graph edits ???
  // hierarchy mutation with non-uniform scales shears across rig conventions):
  //   D_j = T(ourJoint_m) . Stretch(ourAxis_m, r_j) . R(G.restDir_j -> ourAxis_m) . G . T(-restJoint_j)
  // where G is a global yaw turning the rest pose to face +Z.
  const restJoint = skeleton.bones.map((b) => b.getWorldPosition(new THREE.Vector3()));

  // Rest segment direction per bone: mean over mapped children; childless feet
  // use the foot-vertex centroid; otherwise the parent segment.
  const restDir: (THREE.Vector3 | null)[] = skeleton.bones.map((b, j) => {
    const dir = new THREE.Vector3();
    let used = 0;
    for (const c of childBones(b)) {
      const cj = skeleton.bones.indexOf(c);
      if (cj < 0 || !directlyMapped[cj] || boneOurIndex[cj] === boneOurIndex[j]) continue; if (boneOurIndex[j] === hipsIdx && boneOurIndex[cj] !== spineIdx) continue;
      dir.add(restJoint[cj].clone().sub(restJoint[j]));
      used++;
    }
    if (used === 0 && footRestDir.has(j)) dir.copy(footRestDir.get(j)!);
    if (dir.lengthSq() < 1e-10 && b.parent && (b.parent as THREE.Bone).isBone) {
      const pj = skeleton.bones.indexOf(b.parent as THREE.Bone);
      if (pj >= 0) dir.subVectors(restJoint[j], restJoint[pj]);
    }
    return dir.lengthSq() > 1e-10 ? dir.normalize() : null;
  });
  // OUR bone indices the mapped children resolve to (same filter as restLen).
  // When the source rig SKIPS an intermediate bone we have (e.g. a VRM with no
  // UpperChest: its Chest binds straight to Neck/Shoulders), a source bone's
  // child maps to a NON-direct descendant of ours. oLen must then measure OUR
  // span to that same descendant, or the axial-stretch ratio is computed
  // against mismatched spans and crushes the torso (Chest squashed to ~0.36).
  const mappedChildOur: number[][] = skeleton.bones.map((b, j) => {
    const out: number[] = [];
    for (const c of childBones(b)) {
      const cj = skeleton.bones.indexOf(c);
      if (cj < 0 || !directlyMapped[cj] || boneOurIndex[cj] === boneOurIndex[j]) continue;
      if (boneOurIndex[j] === hipsIdx && boneOurIndex[cj] !== spineIdx) continue;
      out.push(boneOurIndex[cj]);
    }
    return out;
  });
  const restLen = skeleton.bones.map((b, j) => {
    const ls: number[] = [];
    for (const c of childBones(b)) {
      const cj = skeleton.bones.indexOf(c);
      if (cj < 0 || !directlyMapped[cj] || boneOurIndex[cj] === boneOurIndex[j]) continue; if (boneOurIndex[j] === hipsIdx && boneOurIndex[cj] !== spineIdx) continue;
      ls.push(restJoint[cj].distanceTo(restJoint[j]));
    }
    if (ls.length === 0 && b.parent && (b.parent as THREE.Bone).isBone) {
      const pj = skeleton.bones.indexOf(b.parent as THREE.Bone);
      if (pj >= 0) ls.push(restJoint[pj].distanceTo(restJoint[j]));
    }
    return mean(ls);
  });
  // OUR segment direction toward the SAME joints the source's mapped children
  // resolve to. The alignment rotation maps the source rest direction onto
  // this; if the source skips a bone we have (no UpperChest), its rest dir
  // averages over Neck+shoulders, so the target must too — otherwise the
  // mismatched tilt pitches the upper spine ("the spine looks rotated").
  // Falls back (empty) to ourAxis[m]; identical to it on 1:1 rigs.
  const ourDirForBone: (THREE.Vector3 | null)[] = skeleton.bones.map((_, j) => {
    const kids = mappedChildOur[j];
    if (!kids.length) return null;
    const m = boneOurIndex[j];
    const dir = new THREE.Vector3();
    for (const ci of kids) {
      dir.add(new THREE.Vector3(
        ourWorld[ci][0] - ourWorld[m][0],
        ourWorld[ci][1] - ourWorld[m][1],
        ourWorld[ci][2] - ourWorld[m][2],
      ));
    }
    return dir.lengthSq() > 1e-10 ? dir.normalize() : null;
  });

  // Global yaw: rest forward heading (toes relative to feet, or foot-vertex
  // centroids) turned to +Z.
  const G = new THREE.Matrix4();
  {
    const heading = new THREE.Vector3();
    skeleton.bones.forEach((b, j) => {
      if (!directlyMapped[j]) return;
      const u = unityNames[boneOurIndex[j]];
      if (u !== "LeftToes" && u !== "RightToes") return;
      const parentBone = b.parent as THREE.Bone;
      if (!parentBone?.isBone) return;
      const pj = skeleton.bones.indexOf(parentBone);
      if (pj >= 0) heading.add(restJoint[j].clone().sub(restJoint[pj]));
    });
    if (heading.lengthSq() < 1e-8) {
      footRestDir.forEach((d) => heading.add(d));
    }
    heading.y = 0;
    if (heading.lengthSq() > 1e-8) {
      heading.normalize();
      G.makeRotationY(-Math.atan2(heading.x, heading.z));
    }
  }

  // Yawed rest joints/dirs.
  const yawedJoint = restJoint.map((p) => p.clone().applyMatrix4(G));
  const gRot = new THREE.Matrix3().setFromMatrix4(G);
  const yawedDir = restDir.map((d) => (d ? d.clone().applyMatrix3(gRot) : null));

  // T-pose the JOINT TABLE (pure vector math): walk parents-first accumulating
  // per-chain rotations that take each segment to its canonical T-pose
  // direction (arms ??X, spine +Y, legs ???Y, feet keep pitch but head +Z).
  // Without this, A-pose rests would become the exported bind layout and the
  // T-pose rest/HIK characterization would be wrong.
  const canonicalAxis = (unityName: string, cur: THREE.Vector3): THREE.Vector3 | null => {
    if (/^Left(Shoulder|UpperArm|LowerArm|Hand|Thumb|Index|Middle|Ring|Little)/.test(unityName)) {
      return new THREE.Vector3(1, 0, 0);
    }
    if (/^Right(Shoulder|UpperArm|LowerArm|Hand|Thumb|Index|Middle|Ring|Little)/.test(unityName)) {
      return new THREE.Vector3(-1, 0, 0);
    }
    if (/^(Spine|Chest|UpperChest|Neck)$/.test(unityName)) return new THREE.Vector3(0, 1, 0);
    if (/UpperLeg|LowerLeg/.test(unityName)) return new THREE.Vector3(0, -1, 0);
    if (/Foot|Toes/.test(unityName)) {
      const horiz = Math.hypot(cur.x, cur.z);
      return new THREE.Vector3(0, cur.y, horiz).normalize();
    }
    return null;
  };
  const depthOf = (o: THREE.Object3D) => {
    let d = 0;
    for (let p = o.parent; p; p = p.parent) d++;
    return d;
  };
  const depthOrder = [...skeleton.bones].sort((a, b) => depthOf(a) - depthOf(b));
  const tposeJoint = restJoint.map(() => new THREE.Vector3());
  const chainRot = restJoint.map(() => new THREE.Quaternion());
  for (const b of depthOrder) {
    const j = skeleton.bones.indexOf(b);
    const parentBone = b.parent as THREE.Bone;
    const pj = parentBone?.isBone ? skeleton.bones.indexOf(parentBone) : -1;
    if (pj >= 0) {
      chainRot[j].copy(chainRot[pj]);
      tposeJoint[j]
        .copy(yawedJoint[j])
        .sub(yawedJoint[pj])
        .applyQuaternion(chainRot[j])
        .add(tposeJoint[pj]);
    } else {
      tposeJoint[j].copy(yawedJoint[j]);
    }
    if (!directlyMapped[j]) continue;
    // This bone's own alignment, applied to everything below it.
    const cur = new THREE.Vector3();
    let used = 0;
    for (const c of childBones(b)) {
      const cj = skeleton.bones.indexOf(c);
      if (cj < 0 || !directlyMapped[cj] || boneOurIndex[cj] === boneOurIndex[j]) continue; if (boneOurIndex[j] === hipsIdx && boneOurIndex[cj] !== spineIdx) continue;
      cur.add(yawedJoint[cj].clone().sub(yawedJoint[j]).applyQuaternion(chainRot[j]));
      used++;
    }
    if (used === 0 && yawedDir[j]) cur.copy(yawedDir[j]!).applyQuaternion(chainRot[j]);
    if (cur.lengthSq() < 1e-10) continue;
    cur.normalize();
    const tgt = canonicalAxis(unityNames[boneOurIndex[j]], cur);
    if (tgt) chainRot[j].premultiply(new THREE.Quaternion().setFromUnitVectors(cur, tgt));
  }
  const joints: (Vec3 | null)[] = unityNames.map(() => null);
  skeleton.bones.forEach((_, j) => {
    if (directlyMapped[j] && joints[boneOurIndex[j]] === null) {
      const p = tposeJoint[j];
      joints[boneOurIndex[j]] = [p.x, p.y, p.z];
    }
  });

  // Per-bone world delta matrices.
  const stretchAlong = (a: THREE.Vector3, r: number): THREE.Matrix4 => {
    const k = r - 1;
    return new THREE.Matrix4().set(
      1 + k * a.x * a.x, k * a.x * a.y, k * a.x * a.z, 0,
      k * a.y * a.x, 1 + k * a.y * a.y, k * a.y * a.z, 0,
      k * a.z * a.x, k * a.z * a.y, 1 + k * a.z * a.z, 0,
      0, 0, 0, 1,
    );
  };
  const boneDelta: (THREE.Matrix4 | null)[] = skeleton.bones.map((_, j) => {
    if (!directlyMapped[j]) return null; // inherit from the mapped ancestor below
    const m = boneOurIndex[j];
    const D = new THREE.Matrix4().makeTranslation(-restJoint[j].x, -restJoint[j].y, -restJoint[j].z);
    D.premultiply(G);
    const dst = ourDirForBone[j] ?? ourAxis[m];
    const dir = yawedDir[j];
    // Head/eyes transfer RIGIDLY (no axial stretch) ??? a head should keep its
    // authored scale (a VRM head was visibly shrunk by the eye-segment ratio).
    const rigid = /Head|Eye/.test(unityNames[m]);
    if (dst && dir) {
      const q = new THREE.Quaternion().setFromUnitVectors(dir, dst);
      D.premultiply(new THREE.Matrix4().makeRotationFromQuaternion(q));
      // Measure OUR span to the SAME joints restLen used on the source side
      // (spans any bone the source rig skips); fall back to our direct children.
      const kids = mappedChildOur[j].length ? mappedChildOur[j] : ourChildren[m];
      let oLen = mean(kids.map((cm) => segLen(ourWorld[m], ourWorld[cm])));
      if (oLen < 1e-4 && parents[m] >= 0) oLen = segLen(ourWorld[parents[m]], ourWorld[m]);
      if (!rigid && oLen > 1e-4 && restLen[j] > 1e-4) {
        const r = Math.min(4, Math.max(0.1, oLen / restLen[j]));
        D.premultiply(stretchAlong(dst, r));
      }
    }
    D.premultiply(new THREE.Matrix4().makeTranslation(ourWorld[m][0], ourWorld[m][1], ourWorld[m][2]));
    return D;
  });
  // Unmapped helper bones (VRM hair/skirt spring chains, finger tips,
  // HeadTop_End???) ride RIGIDLY with their nearest mapped ancestor ??? giving
  // them their own rotation deltas shreds hair into ribbons.
  skeleton.bones.forEach((b, j) => {
    if (boneDelta[j]) return;
    let cur: THREE.Object3D | null = b.parent;
    while (cur) {
      const pj = skeleton.bones.indexOf(cur as THREE.Bone);
      if (pj >= 0 && boneDelta[pj]) {
        boneDelta[j] = boneDelta[pj];
        return;
      }
      cur = cur.parent;
    }
    boneDelta[j] = new THREE.Matrix4();
  });


  // --- 4. deform: world-space per-bone deltas applied to the captured rest
  // vertices, blended by the authored weights. Convention-free: only world
  // matrices are involved, never the asset's bind matrices.
  const meshes: BodyMeshData[] = [];
  for (let mi2 = 0; mi2 < skinnedMeshes.length; mi2++) {
    const m = skinnedMeshes[mi2];
    // Modular packs ship the swappable head as separate sub-meshes (incl.
    // earrings/glasses) ??? skip the whole section; the Face mesh replaces it.
    if (!opts.keepHead && /(^|_)(Head|Ears?)(_|$)/i.test(m.name)) continue;
    const rest = restWorldVerts[mi2];
    const geo = m.geometry;
    const sIdx = geo.getAttribute("skinIndex");
    const sWgt = geo.getAttribute("skinWeight");
    const count = rest.length / 3;

    const outPos = new Float32Array(count * 3);
    const outIdx = new Uint16Array(count * 4);
    const outWgt = new Float32Array(count * 4);
    const headWeight = new Float32Array(count);

    const v = new THREE.Vector3();
    const acc = new THREE.Vector3();
    const l2u = localToUnion(m);
    const morphDict = (m as THREE.SkinnedMesh & { morphTargetDictionary?: Record<string, number> })
      .morphTargetDictionary;
    const wantMorphs = !!(opts.keepHead && opts.morphNames?.length && morphDict);
    // Per-vertex blended LINEAR part of the deltas (column-major 3??3), needed
    // to carry morph delta vectors through the same transfer as positions.
    const linAcc = wantMorphs ? new Float32Array(count * 9) : null;

    for (let i = 0; i < count; i++) {
      acc.set(0, 0, 0);
      let wsum = 0;
      for (let k = 0; k < 4; k++) {
        const j = l2u[sIdx.getComponent(i, k)];
        const w = sWgt.getComponent(i, k);
        if (w === 0 || j < 0) continue;
        v.set(rest[i * 3], rest[i * 3 + 1], rest[i * 3 + 2]).applyMatrix4(boneDelta[j]!);
        acc.addScaledVector(v, w);
        wsum += w;
        outIdx[i * 4 + k] = boneOurIndex[j];
        outWgt[i * 4 + k] = w;
        if (boneIsHead[j]) headWeight[i] += w;
        if (linAcc) {
          const e = boneDelta[j]!.elements;
          const o = i * 9;
          linAcc[o] += w * e[0]; linAcc[o + 1] += w * e[1]; linAcc[o + 2] += w * e[2];
          linAcc[o + 3] += w * e[4]; linAcc[o + 4] += w * e[5]; linAcc[o + 5] += w * e[6];
          linAcc[o + 6] += w * e[8]; linAcc[o + 7] += w * e[9]; linAcc[o + 8] += w * e[10];
        }
      }
      if (wsum > 1e-6) acc.multiplyScalar(1 / wsum);
      else acc.set(rest[i * 3], rest[i * 3 + 1], rest[i * 3 + 2]);
      outPos[i * 3] = acc.x; outPos[i * 3 + 1] = acc.y; outPos[i * 3 + 2] = acc.z;
    }

    // Drop only the HEAD geometry: triangles whose verts are all weighted to
    // head bones AND sit above the head joint (keeps the whole neck; the cut
    // follows the mesh's horizontal loop at the head joint and hides inside
    // the Face mesh). Then compact verts.
    const cutY = ourWorld[headIdx] ? ourWorld[headIdx][1] - 0.015 : Infinity;
    const srcIndices = geo.index
      ? (geo.index.array as ArrayLike<number>)
      : Array.from({ length: count }, (_, i) => i);
    const keptTris: number[] = [];
    // Weight-only cut: a positional condition keeps the jaw/chin (head-
    // weighted but below the head joint) poking out under the Face mesh.
    void cutY;
    const aboveCut = (vi: number) => !opts.keepHead && headWeight[vi] > 0.5;
    for (let t = 0; t < srcIndices.length; t += 3) {
      const a = srcIndices[t], b2 = srcIndices[t + 1], c2 = srcIndices[t + 2];
      // Drop tris touching ANY removed head vert - keeping boundary-crossing
      // tris leaves upward spikes around the neck.
      if (aboveCut(a) || aboveCut(b2) || aboveCut(c2)) continue;
      keptTris.push(a, b2, c2);
    }
    const remap = new Int32Array(count).fill(-1);
    let next = 0;
    for (const vi of keptTris) if (remap[vi] < 0) remap[vi] = next++;
    if (next === 0) continue;
    const positions = new Float32Array(next * 3);
    const skinIndex = new Uint16Array(next * 4);
    const skinWeight = new Float32Array(next * 4);
    const uvAttr = geo.getAttribute("uv");
    const uv = uvAttr ? new Float32Array(next * 2) : undefined;
    for (let i = 0; i < count; i++) {
      const r = remap[i];
      if (r < 0) continue;
      positions.set(outPos.subarray(i * 3, i * 3 + 3), r * 3);
      skinIndex.set(outIdx.subarray(i * 4, i * 4 + 4), r * 4);
      skinWeight.set(outWgt.subarray(i * 4, i * 4 + 4), r * 4);
      if (uv && uvAttr) {
        uv[r * 2] = uvAttr.getX(i);
        uv[r * 2 + 1] = uvAttr.getY(i);
      }
    }
    const indices = new Uint32Array(keptTris.length);
    for (let t = 0; t < keptTris.length; t++) indices[t] = remap[keptTris[t]];

    // Recompute smooth normals on the transferred geometry.
    const tmpGeo = new THREE.BufferGeometry();
    tmpGeo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    tmpGeo.setIndex(new THREE.Uint32BufferAttribute(indices, 1));
    tmpGeo.computeVertexNormals();
    const normals = new Float32Array(tmpGeo.getAttribute("normal").array as ArrayLike<number>);
    tmpGeo.dispose();

    // Morph channels: match the recorded blendshape names to this mesh's
    // morph targets (case-insensitive) and carry the deltas through the same
    // per-vertex linear transform as the positions.
    let channels: { name: string; deltas: Float32Array }[] | undefined;
    if (linAcc && morphDict) {
      const meshLin = new THREE.Matrix3().setFromMatrix4(m.matrixWorld);
      const dictLower = new Map(Object.keys(morphDict).map((k2) => [k2.toLowerCase(), k2]));
      channels = [];
      for (const recName of opts.morphNames!) {
        const key = dictLower.get(recName.toLowerCase());
        if (key === undefined) continue;
        const attr = geo.morphAttributes.position?.[morphDict[key]];
        if (!attr) continue;
        const deltas = new Float32Array(next * 3);
        let moved = 0;
        for (let i = 0; i < count; i++) {
          const r = remap[i];
          if (r < 0) continue;
          v.fromBufferAttribute(attr, i).applyMatrix3(meshLin);
          const o = i * 9;
          const dx = linAcc[o] * v.x + linAcc[o + 3] * v.y + linAcc[o + 6] * v.z;
          const dy = linAcc[o + 1] * v.x + linAcc[o + 4] * v.y + linAcc[o + 7] * v.z;
          const dz = linAcc[o + 2] * v.x + linAcc[o + 5] * v.y + linAcc[o + 8] * v.z;
          deltas[r * 3] = dx; deltas[r * 3 + 1] = dy; deltas[r * 3 + 2] = dz;
          moved = Math.max(moved, Math.abs(dx), Math.abs(dy), Math.abs(dz));
        }
        if (moved > 1e-6) channels.push({ name: recName, deltas });
      }
      if (channels.length === 0) channels = undefined;
    }

    const srcMaterial = (Array.isArray(m.material) ? m.material[0] : m.material) as
      | (THREE.Material & { map?: THREE.Texture | null })
      | undefined;
    meshes.push({
      name: meshes.length === 0 ? "Body" : `Body${meshes.length + 1}`,
      positions,
      normals,
      indices,
      skinIndex,
      skinWeight,
      channels,
      uv,
      map: srcMaterial?.map ?? null,
      texture: opts.textures?.get(m),
    });
  }

  return { meshes, joints };
}


/** Convert preview-space body data (meters) into FBX skinned-mesh exports (cm). */
export function bodyToSkinnedMeshExports(
  meshes: BodyMeshData[],
  face?: { names: string[]; tracks: Float32Array[] },
): SkinnedMeshExport[] {
  return meshes.map((m) => {
    const positions = new Float64Array(m.positions.length);
    for (let i = 0; i < m.positions.length; i++) positions[i] = m.positions[i] * 100;
    const normals = new Float64Array(m.normals.length);
    normals.set(m.normals);

    // Gather per-bone clusters; merge duplicate influences per vertex.
    const byBone = new Map<number, Map<number, number>>();
    const count = m.positions.length / 3;
    for (let i = 0; i < count; i++) {
      for (let k = 0; k < 4; k++) {
        const w = m.skinWeight[i * 4 + k];
        if (w < 1e-4) continue;
        const bone = m.skinIndex[i * 4 + k];
        let entry = byBone.get(bone);
        if (!entry) byBone.set(bone, (entry = new Map()));
        entry.set(i, (entry.get(i) ?? 0) + w);
      }
    }
    const clusters = [...byBone.entries()].map(([boneIndex, perVert]) => ({
      boneIndex,
      pointIndices: Int32Array.from(perVert.keys()),
      weights: Float64Array.from(perVert.values()),
    }));

    // Morph channels: deltas to cm, paired with the recorded weight tracks.
    let channels: SkinnedMeshExport["channels"];
    if (m.channels && face) {
      channels = [];
      for (const ch of m.channels) {
        const n = face.names.indexOf(ch.name);
        if (n < 0) continue;
        const weights = face.tracks[n];
        let moved = 0;
        for (let i = 0; i < weights.length; i++) moved = Math.max(moved, Math.abs(weights[i]));
        if (moved < 0.01) continue;
        const deltas = new Float64Array(ch.deltas.length);
        for (let i = 0; i < ch.deltas.length; i++) deltas[i] = ch.deltas[i] * 100;
        channels.push({ name: ch.name, deltas, weights });
      }
      if (channels.length === 0) channels = undefined;
    }

    // UVs: glTF puts (0,0) top-left; FBX texture space is bottom-left → flip V.
    let uv: Float64Array | undefined;
    if (m.uv && m.texture) {
      uv = new Float64Array(m.uv.length);
      for (let i = 0; i < m.uv.length; i += 2) {
        uv[i] = m.uv[i];
        uv[i + 1] = 1 - m.uv[i + 1];
      }
    }

    return {
      name: m.name,
      positions,
      normals,
      indices: m.indices,
      clusters,
      channels,
      uv,
      texture: m.texture,
    };
  });
}

/** Browser-side cached loader for the body-skeleton joints (for proportions). */
export function getBodyJoints(
  parents: number[],
  bindPos: Vec3[],
  unityNames: string[],
): Promise<(Vec3 | null)[]> {
  return buildBodyData(parents, bindPos, unityNames).then((d) => d.joints);
}
